import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import ReviewSection from "./ReviewSection";
import BusinessReviewsList from "./BusinessReviewsList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Product-page review block:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ Customer Reviews              [WRITE A REVIEW] (if Pr)  │
 *   │  ┌─Business Reviews(n)─┐ ┌─Product Reviews(m)─┐         │
 *   │  ├──────────────────────┴──────────────────────┴──────  │
 *   │  <active tab content>                                    │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Default tab:  product if product count > 0 else business.
 * Empty-states:
 *   - 0 biz + n product → no tabs, just legacy Customer Reviews block.
 *   - n biz + 0 product → tabs render; Product tab shows the existing
 *                         "be the first to review" form (so customers can
 *                         still submit), Business tab shows Google reviews.
 */
export default function ReviewsTabs({ outfitId, productReviews, onRefreshProduct }) {
  const [biz, setBiz] = useState(null);
  const [bizLoaded, setBizLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/business-reviews`)
      .then((res) => { if (!cancelled) setBiz(res.data); })
      .catch(() => { if (!cancelled) setBiz(null); })
      .finally(() => { if (!cancelled) setBizLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const productCount = (productReviews && productReviews.count) || 0;
  const businessCount = (biz && biz.count) || 0;
  const showBusinessTab = bizLoaded && businessCount > 0;

  // Default to Product if any product reviews exist, otherwise Business.
  const [tab, setTab] = useState("product");
  useEffect(() => {
    if (productCount > 0) setTab("product");
    else if (showBusinessTab) setTab("business");
  }, [productCount, showBusinessTab]);

  // Batch 9 — when arriving from the review-request email (`#review` hash or
  // `?review=1` query) force-switch to the Product tab + open the write form,
  // regardless of how many product reviews already exist.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    if (!/(^|[#?&])review(=|$)/i.test(hash + search)) return;
    setTab("product");
    setShowForm(true);
  }, []);

  // No business reviews at all → render the original Customer Reviews block unchanged.
  if (!showBusinessTab) {
    return (
      <ReviewSection
        outfitId={outfitId}
        reviews={productReviews}
        onRefresh={onRefreshProduct}
        externalShowForm={showForm || undefined}
        onShowFormChange={setShowForm}
      />
    );
  }

  const isProductTab = tab === "product";
  const canShowWriteBtn = isProductTab && !showForm;

  const TabButton = ({ id, label, count }) => {
    const active = tab === id;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setTab(id)}
        className={`relative px-4 sm:px-5 py-2.5 text-[12px] tracking-[0.06em] uppercase transition-colors flex-1 sm:flex-none text-left ${
          active
            ? "text-[#282828] font-medium"
            : "text-[#676664]/55 hover:text-[#282828]"
        }`}
        data-testid={`reviews-tab-${id}`}
      >
        <span>{label}</span>
        <span className={`ml-1.5 text-[11px] ${active ? "text-[#9A7B4F]" : "text-[#676664]/40"}`}>
          ({count})
        </span>
        {/* Active bottom border — sits on top of the container's bottom border */}
        <span
          aria-hidden="true"
          className={`absolute left-0 right-0 -bottom-px h-[2px] ${active ? "bg-[#9A7B4F]" : "bg-transparent"}`}
        />
      </button>
    );
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#282828]/8" data-testid="reviews-tabs">
      {/* Header — heading on left, conditional Write button on right */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <h3
          className="text-lg lg:text-xl text-[#1A1A1A]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Customer Reviews
        </h3>
        {canShowWriteBtn && (
          <Button
            variant="outline"
            onClick={() => setShowForm(true)}
            className="text-[10px] tracking-[0.1em] uppercase border-[#282828]/12 text-[#676664] hover:border-[#9A7B4F]/30"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div
        role="tablist"
        className="flex items-end border-b border-[#282828]/10 mb-5"
      >
        <TabButton id="business" label="Business Reviews" count={businessCount} />
        <TabButton id="product" label="Product Reviews" count={productCount} />
      </div>

      {/* Tab content */}
      <div role="tabpanel" data-testid={`reviews-panel-${tab}`}>
        {isProductTab ? (
          <ReviewSection
            outfitId={outfitId}
            reviews={productReviews}
            onRefresh={onRefreshProduct}
            hideHeader
            externalShowForm={showForm}
            onShowFormChange={setShowForm}
          />
        ) : (
          <BusinessReviewsList data={biz} />
        )}
      </div>
    </div>
  );
}
